'use client';

import { createContext } from 'react';

import type { BioContext } from '@ps/functions/bio/bioContext';
import type { BehaviorSubject } from 'rxjs';

import type { ChatRoomJoinState } from '../../hooks/useJoinChatRoom';
import type { ChatMessage } from '../../types/ChatMessage';
import type { ChatSessionType } from '../../types/ChatSessionType';
import type { ChatUser } from '../../types/ChatUser';

/**
 * Per-room context value — the chat-detail equivalent of the global
 * sessions context. Holds everything a single chat needs to render:
 * the room identity, both users, join state, and the in-flight
 * "quoted message" (reply target).
 */
export interface ChatContextValue {
  sessionType: ChatSessionType;
  roomId: string;
  sessionId: string;
  currentUserId: string;
  targetUserId: string;
  currentUser: ChatUser;
  targetUser: ChatUser;
  users: ChatUser[];
  /** True for temp-chats that shouldn't show up in the sessions list. */
  transient?: boolean;
  /** Whether the back button should render in the room header. */
  backButton?: boolean;
  /**
   * The chat's bio context, or `null` on the non-1:1 DM inbox (`/messaging`,
   * `/messages`) where DMs from different creators mix and there's no single
   * creator scope. A 1:1 creator chat + community member chat carry a real one.
   * Threaded to shared post cards so they build bio URLs / subscribe correctly.
   */
  bioContext: BioContext | null;
  /** Message the next send should quote, or undefined for no reply. */
  quotedMessage$: BehaviorSubject<ChatMessage | undefined>;
  joinState: ChatRoomJoinState;
}

export const ChatContext = createContext<ChatContextValue | null>(null);
